/**
 * Typed binding for the /admin/entities/* endpoints (PR #5a.1).
 *
 * The shape mirrors what the AdminModule's Entities tab needs:
 *   - list(filter) → backing the left pane of the master/detail view.
 *   - get(id)      → fetching a single entity when an admin clicks a row.
 *   - create(body) → "+ New" button on the Entities tab.
 *   - update(id, body) → save edits in place; PATCH semantics.
 *
 * All four endpoints require an authenticated session AND
 * `admin.entity.manage`. Wire errors that surface here include
 * `entity_type_not_found`, `entity_name_taken`, `forbidden`,
 * `unauthorized`, and `not_found`.
 */

import {
  EntityListResponseSchema,
  EntityResponseSchema,
  type EntityCreateRequest,
  type EntityListQuery,
  type EntityListResponse,
  type EntityResponse,
  type EntityUpdateRequest,
} from '../schemas/entity.js';
import type { RequestOptions, Transport } from './transport.js';

export interface EntitiesClient {
  list(query?: EntityListQuery, options?: RequestOptions): Promise<EntityListResponse>;
  get(id: string, options?: RequestOptions): Promise<EntityResponse>;
  create(body: EntityCreateRequest, options?: RequestOptions): Promise<EntityResponse>;
  update(id: string, body: EntityUpdateRequest, options?: RequestOptions): Promise<EntityResponse>;
}

const buildListPath = (query: EntityListQuery | undefined): string => {
  if (!query) return '/admin/entities';
  const params = new URLSearchParams();
  if (query.entityTypeSlug !== undefined) params.set('entityTypeSlug', query.entityTypeSlug);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.search !== undefined) params.set('search', query.search);
  const qs = params.toString();
  return qs.length === 0 ? '/admin/entities' : `/admin/entities?${qs}`;
};

export const createEntitiesClient = (transport: Transport): EntitiesClient => ({
  list: (query, options) => transport.get(buildListPath(query), EntityListResponseSchema, options),
  get: (id, options) =>
    transport.get(`/admin/entities/${encodeURIComponent(id)}`, EntityResponseSchema, options),
  create: (body, options) => transport.post('/admin/entities', EntityResponseSchema, body, options),
  update: (id, body, options) =>
    transport.patch(
      `/admin/entities/${encodeURIComponent(id)}`,
      EntityResponseSchema,
      body,
      options,
    ),
});
